import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttachmentStage,
  TicketStatus,
  attachmentStageLabelKey,
  type TicketAttachment,
} from '@nbh/shared';
import { AttachmentImage } from '../ui/AttachmentImage';
import { PhotoLightbox } from './PhotoLightbox';
import { altKeyFor, firstPhotoAtStage } from '../../lib/attachments';

export interface TicketPhotoComparisonProps {
  attachments: TicketAttachment[];
  status: TicketStatus;
}

/**
 * Before and after, side by side, for the person signing the work off (R6).
 *
 * Two panes rather than two stacked images: the question a verifier is answering is
 * "is that the same tap, and is it fixed", and a scroll between the two turns it into a
 * memory test. At 360px each pane is about 170px wide, which is enough to tell a dry
 * joint from a wet one; the whole frame is one tap away in the viewer.
 *
 * Cropped to a shared square so the two align. That does hide the edges of a wide shot,
 * which is exactly why tapping either pane opens it uncropped.
 *
 * **Renders nothing unless both photos exist.** Not a half comparison, not an empty
 * slot, not a note about which one is missing: the verify flow is identical on a job
 * that never had a photo, and nothing here may imply the verifier is owed one (D2).
 */
export function TicketPhotoComparison({ attachments, status }: TicketPhotoComparisonProps) {
  const { t } = useTranslation();
  const [viewing, setViewing] = useState<number | null>(null);

  const issue = firstPhotoAtStage(attachments, AttachmentStage.Issue);
  const completion = firstPhotoAtStage(attachments, AttachmentStage.Completion);
  if (status !== TicketStatus.Resolved || !issue || !completion) return null;

  const panes = [
    { photo: issue, stage: AttachmentStage.Issue },
    { photo: completion, stage: AttachmentStage.Completion },
  ];

  return (
    <section className="mt-3">
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-slate-400 uppercase">
        {t('attachment.comparison')}
      </h2>

      <div className="grid grid-cols-2 gap-2">
        {panes.map(({ photo, stage }, index) => (
          <figure key={photo.id} className="m-0">
            <button
              type="button"
              onClick={() => setViewing(index)}
              aria-label={t('attachment.open')}
              className="block w-full overflow-hidden rounded-lg bg-transparent p-0"
            >
              <AttachmentImage
                src={photo.url}
                alt={t(altKeyFor(stage))}
                width={photo.width}
                height={photo.height}
                className="aspect-square w-full rounded-lg object-cover"
              />
            </button>
            <figcaption className="mt-1 text-center text-xs font-medium text-slate-300">
              {t(attachmentStageLabelKey[stage])}
            </figcaption>
          </figure>
        ))}
      </div>

      {viewing !== null && (
        <PhotoLightbox
          photos={[issue, completion]}
          startIndex={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </section>
  );
}
